import type { CampingSpot, Coordinates } from '../types';
import { getDistance } from './geometryUtils';

// Merk: bevisst IKKE import fra occupancyService — den drar inn firebaseService
// (initializeApp ved import), som knekker testene. Occupancy-sjekken er en triviell
// enlinjer; kilden til sannhet er `isSpotOccupied` i occupancyService.
const isOccupied = (spot: CampingSpot, now: number): boolean =>
  !!spot.occupiedUntil && spot.occupiedUntil > now;

/** Terskel for at en bruker regnes som «på» plassen (innsjekk-avstand). */
export const NEARBY_THRESHOLD_KM = 0.15; // 150 m

/** Hvor lenge brukeren må ha oppholdt seg ved en plass før dwell-innsjekk-pushen fyres. */
export const PROXIMITY_DWELL_MS = 30 * 60 * 1000; // 30 min

/** localStorage-nøkkel for dwell-timeren (delt av aktiv sporing og passiv watch). */
export const PROXIMITY_TIMER_STORAGE_KEY = 'fricamp_proximity_timer_v1';

/**
 * Effektiv avstand til en plass i kilometer.
 *
 * Foretrekker en forhåndsberegnet `spot.distance` (settes f.eks. av
 * distansesortert liste), men faller tilbake til å regne ut avstanden fra
 * brukerens GPS-posisjon. Fallbacken er nødvendig for bunnkortet, som ellers
 * aldri får en `distance` — bl.a. rett etter at en plass er opprettet via
 * kart-trykk — og dermed skjulte innsjekk-knappen for ferske plasser.
 *
 * Returnerer `null` når avstanden ikke kan bestemmes (verken forhåndsberegnet
 * verdi eller GPS + plasskoordinat tilgjengelig).
 */
export const spotDistanceKm = (
  spot: (CampingSpot & { distance?: number }) | null | undefined,
  userPosition?: Coordinates | null,
): number | null => {
  if (typeof spot?.distance === 'number') return spot.distance;
  if (userPosition && spot?.location) {
    return getDistance(
      userPosition.latitude,
      userPosition.longitude,
      spot.location.latitude,
      spot.location.longitude,
    ) / 1000;
  }
  return null;
};

/** Er brukeren nære nok plassen til å kunne sjekke inn? */
export const isSpotNearby = (
  spot: (CampingSpot & { distance?: number }) | null | undefined,
  userPosition?: Coordinates | null,
  thresholdKm: number = NEARBY_THRESHOLD_KM,
): boolean => {
  const dist = spotDistanceKm(spot, userPosition);
  return dist != null && dist <= thresholdKm;
};

// --- Dwell-basert innsjekk-push -------------------------------------------
// Brukeren har oppholdt seg ved en plass en stund → be om innsjekk. Logikken er
// delt mellom aktiv turlogg-sporing (useGpsTracking) og den passive idle-watchen
// (App.tsx) slik at én sammenhengende timer gjelder uansett hvilken GPS-kilde som
// mater den. Samme 150 m-terskel som resten av innsjekk-UI (NEARBY_THRESHOLD_KM);
// den lange oppholdstiden (PROXIMITY_DWELL_MS) filtrerer bort forbipasseringer.

export interface ProximityTimer {
  spotId: string;
  enteredAt: number;
  notified: boolean;
}

export interface CheckInEligibilityOpts {
  currentUserUid?: string | null;
  isDismissed?: (spotId: string) => boolean;
  now?: number;
}

/**
 * Kan brukeren i det hele tatt bli bedt om å sjekke inn på denne plassen?
 * Ekskluderer plasser uten koordinat, forbudsområder, plasser brukeren allerede
 * har sjekket inn på selv, og plasser som nylig er avvist. Delt av forgrunns-dwell
 * (findNearbyCheckInSpot) og bakgrunns-geofence-utvalget (geofenceManager).
 */
export const isCheckInEligible = (
  spot: CampingSpot,
  opts: CheckInEligibilityOpts = {},
): boolean => {
  const { currentUserUid, isDismissed, now = Date.now() } = opts;
  if (!spot.location) return false;
  if (spot.spotType === 'forbidden_area') return false;
  if (isOccupied(spot, now) && spot.occupiedBy === currentUserUid) return false;
  if (isDismissed?.(spot.id)) return false;
  return true;
};

/**
 * Finn en plass brukeren står på og kan sjekke inn på (dwell-push-kandidat).
 * Returnerer `null` uten posisjon eller treff.
 */
export const findNearbyCheckInSpot = (
  position: Coordinates | null | undefined,
  spots: CampingSpot[],
  opts: {
    currentUserUid?: string | null;
    isDismissed?: (spotId: string) => boolean;
    thresholdKm?: number;
    now?: number;
  } = {},
): CampingSpot | null => {
  if (!position) return null;
  const { thresholdKm = NEARBY_THRESHOLD_KM } = opts;
  const radiusM = thresholdKm * 1000;
  return (
    spots.find(s => {
      if (!isCheckInEligible(s, opts)) return false;
      const dist = getDistance(position.latitude, position.longitude, s.location!.latitude, s.location!.longitude);
      return dist <= radiusM;
    }) ?? null
  );
};

export type DwellDecision =
  | { kind: 'clear' }                          // ingen kandidat → nullstill timer
  | { kind: 'start'; timer: ProximityTimer }   // ny plass → start opphold-timer
  | { kind: 'wait'; timer: ProximityTimer }    // teller fortsatt, eller allerede varslet
  | { kind: 'notify'; timer: ProximityTimer }; // opphold nådd → fyr push (og marker varslet)

/**
 * Ren beslutning: gitt dagens kandidat, forrige timer-tilstand og «nå», hva skal
 * skje? Ingen sideeffekter — kaller lagrer/varsler basert på `kind`.
 */
export const decideCheckInAction = (
  candidate: CampingSpot | null,
  timer: ProximityTimer | null,
  now: number,
  dwellMs: number = PROXIMITY_DWELL_MS,
): DwellDecision => {
  if (!candidate) return { kind: 'clear' };
  if (!timer || timer.spotId !== candidate.id) {
    return { kind: 'start', timer: { spotId: candidate.id, enteredAt: now, notified: false } };
  }
  if (!timer.notified && now - timer.enteredAt >= dwellMs) {
    return { kind: 'notify', timer: { ...timer, notified: true } };
  }
  return { kind: 'wait', timer };
};

/**
 * Orkestrator: les timer fra localStorage, avgjør handling, skriv tilbake og fyr
 * push ved behov. `notify` injiseres (Capacitor-fritt her) slik at modulen forblir
 * testbar. Trygg å kalle på hvert GPS-punkt.
 */
export const runProximityCheckIn = (
  position: Coordinates | null | undefined,
  spots: CampingSpot[],
  opts: {
    currentUserUid?: string | null;
    isDismissed?: (spotId: string) => boolean;
    notify: (spot: CampingSpot) => void;
    now?: number;
  },
): void => {
  const now = opts.now ?? Date.now();
  const candidate = findNearbyCheckInSpot(position, spots, { ...opts, now });

  let timer: ProximityTimer | null = null;
  try {
    const raw = localStorage.getItem(PROXIMITY_TIMER_STORAGE_KEY);
    timer = raw ? JSON.parse(raw) : null;
  } catch { timer = null; }

  const decision = decideCheckInAction(candidate, timer, now);

  try {
    if (decision.kind === 'clear') {
      localStorage.removeItem(PROXIMITY_TIMER_STORAGE_KEY);
    } else {
      localStorage.setItem(PROXIMITY_TIMER_STORAGE_KEY, JSON.stringify(decision.timer));
    }
  } catch { /* ignorer quota-/serialiseringsfeil */ }

  if (decision.kind === 'notify' && candidate) {
    opts.notify(candidate);
  }
};
